import { Icon } from '@iconify/react';
import trailerImg from '../../assets/images/viewImg.png';
import report from '../../assets/images/report.png';
import smsImg from '../../assets/images/sms.png';
import { useState } from 'react';
import image1 from '../../assets/images/image1.png';
import completeImg from '../../assets/images/lets-icons_check-fill.png';
import { Inspection } from '../../services/inspection.service';
import { format } from 'date-fns';
import CriticalSummaryModal from './CriticalSummaryModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/reducers';

interface ViewReportProps {
    onClose: () => void;
    inspection: Inspection | null;
    onInspectionUpdated?: () => void;
}

export default function ViewReport({ onClose, inspection, onInspectionUpdated }: ViewReportProps) {
    const [isFront, setIsFront] = useState(true);
    const [showSummary, setShowSummary] = useState(false);
    
    // Get inspection and checklists from Redux
    const { currentInspection: reduxInspection, currentChecklists } = useSelector((state: RootState) => state.inspection);
    
    // Use inspection prop if provided, otherwise fall back to Redux currentInspection
    const currentInspection = inspection || reduxInspection;
    
    // Get checklists from Redux
    const checklists = currentChecklists || [];

    const handleClose = () => {
        onClose();
    };

    const handleReturnToLog = () => {
        setShowSummary(false);
        // ViewReport will remain open when returning from ViewSummary
    };

    const handleInspectionUpdated = () => {
        // Notify parent component
        if (onInspectionUpdated) {
            onInspectionUpdated();
        }
    };

    // Get failed checklist items
    const failedChecklists = checklists.filter(checklist => checklist.status === 'failed');

    // Check if inspection is completed (no failed checklists means it's completed, acceptable items are considered resolved)
    const isInspectionCompleted = checklists.length > 0 && !checklists.some(checklist => checklist.status === 'failed');

    // If no inspection data is provided, show default/loading state
    if (!currentInspection) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center w-full p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                    <div className="flex justify-between items-center px-6 py-4 border-b border-gray-3">
                        <h2 className="text-black-1 text-xl font-[Geist-Bold]">View Willow Report</h2>
                        <button 
                            onClick={handleClose} 
                            className="w-8 h-8 rounded-full bg-gray-1 hover:bg-gray-2 flex items-center justify-center transition-colors"
                        >
                            <Icon icon="material-symbols:close" className="text-gray-2 text-lg" />
                        </button>
                    </div>
                    <div className="flex items-center justify-center h-64">
                        <div className="text-gray-2">No inspection data available</div>
                    </div>
                </div>
            </div>
        );
    }

    const formattedTimestamp = format(new Date(currentInspection.createdAt), 'MMMM dd, yyyy, hh:mm a');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center w-full p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-3">
                    <h2 className="text-black-1 text-xl font-[Geist-Bold]">View Willow Report</h2>
                    <button 
                        onClick={handleClose} 
                        className="w-8 h-8 rounded-full bg-gray-1 hover:bg-gray-2 flex items-center justify-center transition-colors"
                    >
                        <Icon icon="material-symbols:close" className="text-gray-2 text-lg" />
                    </button>
                </div>

                {/* Info Cards */}
                <div className="px-6 py-4 bg-gray-6">
                    <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-2 text-sm font-[Geist-Medium]">Trailer ID:</span>
                            <span className="px-3 py-1 bg-white border border-gray-3 rounded-lg text-black-1 text-sm font-[Geist-SemiBold]">
                                {currentInspection.trailerId}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-2 text-sm font-[Geist-Medium]">Timestamp:</span>
                            <span className="px-3 py-1 bg-white border border-gray-3 rounded-lg text-black-1 text-sm font-[Geist-SemiBold]">
                                {formattedTimestamp}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Status Alert */}
                    <div className={`mb-6 p-4 rounded-xl border ${
                        failedChecklists.length > 0 
                            ? 'bg-red-50 border-red-200' 
                            : 'bg-green-1 border-green-200'
                    }`}>
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                failedChecklists.length > 0 ? 'bg-red-100' : 'bg-green-100'
                            }`}>
                                <Icon 
                                    icon={failedChecklists.length > 0 ? "material-symbols:warning" : "material-symbols:check-circle"} 
                                    className={`text-lg ${
                                        failedChecklists.length > 0 ? 'text-red-500' : 'text-green'
                                    }`} 
                                />
                            </div>
                            <div>
                                <h3 className={`text-lg font-[Geist-Bold] ${
                                    failedChecklists.length > 0 ? 'text-red-500' : 'text-green'
                                }`}>
                                    {failedChecklists.length > 0 ? 'Critical Event Detected' : 'Inspection Completed'}
                                </h3>
                                <p className="text-black-1 text-sm font-[Geist-Medium] mt-1">
                                    {failedChecklists.length > 0 ? failedChecklists.map(checklist => checklist.name).join(', ') : 'All items are Resolved'}
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* Images Section */}
                    <h3 className="text-lg font-[Geist-Bold] text-black-1 mb-4">Images</h3>
                    <div className="bg-white border border-gray-3 rounded-xl p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {(() => {
                                // Collect all fault photos from all failed checklists
                                const allFaultPhotos: string[] = [];
                                const allPhotos: string[] = [];

                                failedChecklists.forEach(checklist => {
                                    if (checklist.faultPhotosUrls && checklist.faultPhotosUrls.length > 0) {
                                        allFaultPhotos.push(...checklist.faultPhotosUrls);
                                    } else if (checklist.photoUrls && checklist.photoUrls.length > 0) {
                                        allPhotos.push(...checklist.photoUrls);
                                    }
                                });

                                // Use fault photos first, then regular photos, then fallback
                                const photosToShow = allFaultPhotos.length > 0 ? allFaultPhotos : allPhotos;
                                if (photosToShow.length > 0) {
                                    return photosToShow.map((photoUrl: string, index: number) => (
                                        <div key={index} className="rounded-lg overflow-hidden border border-gray-3">
                                            <img
                                                src={photoUrl}
                                                alt={`Fault image ${index + 1}`}
                                                className="max-w-full h-auto"
                                                onError={(e) => {
                                                    e.currentTarget.src = index % 2 === 0 ? image1 : image1;
                                                }}
                                            />
                                        </div>
                                    ));
                                } else {
                                    return (
                                        <div className="col-span-2 text-center py-8">
                                            <Icon icon="material-symbols:image-outline" className="text-4xl text-gray-2 mx-auto mb-2" />
                                            <p className="text-gray-2 text-sm font-[Geist-Medium]">No images available</p>
                                        </div>
                                    );
                                }
                            })()}
                        </div>
                    </div>
                </div>


                {/* SMS Alert */}
                <div className="px-6 py-4 bg-green-1 border border-green-200">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                            <Icon icon="material-symbols:sms" className="text-green text-sm" />
                        </div>
                        <span className="text-black-1 text-sm font-[Geist-Medium]">
                            Automated SMS Alert has been sent to Yard personnel with Report Summary
                        </span>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-3 bg-gray-6">
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={handleClose}
                            disabled={isInspectionCompleted}
                            className="px-6 py-2 text-gray-2 hover:text-black-1 text-sm font-[Geist-Medium] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isInspectionCompleted ? 'Inspection Completed' : 'Cancel'}
                        </button>
                        <button
                            onClick={() => setShowSummary(true)}
                            disabled={isInspectionCompleted}
                            className="px-6 py-2 bg-blue hover:bg-blue-1 text-white rounded-lg text-sm font-[Geist-Medium] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            View Summary
                        </button>
                    </div>
                </div>

            </div>


            {showSummary && <CriticalSummaryModal onClose={() => setShowSummary(false)} inspection={currentInspection} onReturnToLog={handleReturnToLog} onInspectionUpdated={handleInspectionUpdated} />}
        </div>
    );
}
